// src/client/services/loanService.ts
import { ok, err, type ServiceResult, type Ngwee } from '@/lib/types';
import { LOAN_TERM_DAYS, type LoanTermDays } from '@/constants/config';
import { supabase } from '@client/lib/supabase';
import type { LoanFixture, LoanStatus } from '@client/lib/fixtures';
import { computeAllInCost } from '@client/utils/loanUtils';

export interface ApplyLoanInput {
  principal: Ngwee;
  termDays: LoanTermDays;
  // The apply_loan RPC requires a verified payment method. Optional in the
  // input until the apply→confirm UI plumbs in a payment-method picker
  // (deferred). When omitted the Edge Function returns 400.
  paymentMethodId?: string;
  purpose?: string;
}

interface ApplyLoanResponse {
  loan_id: string;
  loan_number: string;
  total_repayable_ngwee: number;
  schedule: Array<{ installment_no: number; due_date: string; scheduled_amount_ngwee: number }>;
}

const ACTIVE_LOAN_STATUSES: LoanStatus[] = [
  'pending_approval',
  'approved',
  'disbursement_pending',
  'active',
  'delinquent',
  'restructured',
];

// Map a `loans` table row (snake_case server columns) to LoanFixture.
// `outstanding_ngwee` is NOT a column on loans — it is calculated from
// loan_repayment_schedule.scheduled - paid. For Sub-B we treat it as
// total_repayable_ngwee, which is correct for new/active loans with no
// partial payments. Sub-C should derive it from the schedule view.
interface LoanRow {
  id: string;
  loan_number: string;
  user_id: string;
  status: LoanStatus;
  principal_ngwee: number;
  interest_ngwee: number;
  fee_ngwee: number;
  total_repayable_ngwee: number;
  term_days: 14 | 30 | 60 | 90;
  applied_at: string;
  disbursed_at: string | null;
  due_date: string | null;
  fully_repaid_at: string | null;
}

function mapLoanRow(row: LoanRow): LoanFixture {
  return {
    id: row.id,
    loan_number: row.loan_number,
    user_id: row.user_id,
    principal_ngwee: row.principal_ngwee as Ngwee,
    interest_ngwee: row.interest_ngwee as Ngwee,
    processing_fee_ngwee: row.fee_ngwee as Ngwee,
    total_due_ngwee: row.total_repayable_ngwee as Ngwee,
    // TODO(Sub-C): derive from loan_repayment_schedule (sum scheduled - paid).
    outstanding_ngwee: row.total_repayable_ngwee as Ngwee,
    term_days: row.term_days,
    status: row.status,
    applied_at: row.applied_at,
    disbursed_at: row.disbursed_at,
    // due_date is a DATE in pg; supabase-js returns it as a 'YYYY-MM-DD' string.
    // Callers that format with formatDueIn() accept either. Pass through.
    due_at: row.due_date,
    repaid_at: row.fully_repaid_at,
  };
}

export async function applyLoan(input: ApplyLoanInput): Promise<ServiceResult<LoanFixture>> {
  if (Number(input.principal) <= 0) return err('invalid_principal');
  if (!LOAN_TERM_DAYS.includes(input.termDays)) return err('invalid_term');

  const body: {
    amount_ngwee: number;
    term_days: number;
    payment_method_id?: string;
    purpose?: string;
  } = {
    amount_ngwee: Number(input.principal),
    term_days: input.termDays,
  };
  if (input.paymentMethodId) body.payment_method_id = input.paymentMethodId;
  if (input.purpose) body.purpose = input.purpose;

  const { data, error } = await supabase.functions.invoke<ApplyLoanResponse>('apply-loan', { body });

  if (error) {
    const status = (error as { context?: { status?: number } }).context?.status ?? 0;
    if (status === 401) return err('unauthorized');
    if (status === 403) return err('forbidden');
    if (status === 404) return err('not_found');
    if (status === 409) return err('concurrent_loan');
    if (status === 412) return err('kyc_not_verified');
    if (status === 429) return err('cooling_off');
    if (status === 400) return err('invalid_input');
    return err('internal_error');
  }
  if (!data) return err('internal_error');

  // Compose a LoanFixture from input + response. The Edge Function only echoes
  // {loan_id, loan_number, total_repayable_ngwee, schedule}; principal/interest/
  // fee/term are taken from the request, status is the initial RPC state.
  const cost = computeAllInCost(input.principal, input.termDays);
  const fixture: LoanFixture = {
    id: data.loan_id,
    loan_number: data.loan_number,
    user_id: '', // not returned; consumers do not rely on it for the apply flow
    principal_ngwee: input.principal,
    interest_ngwee: cost.interest,
    processing_fee_ngwee: cost.processingFee,
    total_due_ngwee: data.total_repayable_ngwee as Ngwee,
    outstanding_ngwee: data.total_repayable_ngwee as Ngwee,
    term_days: input.termDays,
    status: 'pending_approval',
    applied_at: new Date().toISOString(),
    disbursed_at: null,
    due_at: null,
    repaid_at: null,
  };
  return ok(fixture);
}

export async function getActiveLoan(): Promise<ServiceResult<LoanFixture | null>> {
  // RLS scopes loans to the caller's profile, so no .eq('user_id', ...) is needed.
  const { data, error } = await supabase
    .from('loans')
    .select(
      'id, loan_number, user_id, status, principal_ngwee, interest_ngwee, fee_ngwee, total_repayable_ngwee, term_days, applied_at, disbursed_at, due_date, fully_repaid_at',
    )
    .in('status', ACTIVE_LOAN_STATUSES)
    .order('applied_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) return err('internal_error');
  if (!data) return ok(null);
  return ok(mapLoanRow(data as unknown as LoanRow));
}

export async function getLoansHistory(): Promise<ServiceResult<LoanFixture[]>> {
  const { data, error } = await supabase
    .from('loans')
    .select(
      'id, loan_number, user_id, status, principal_ngwee, interest_ngwee, fee_ngwee, total_repayable_ngwee, term_days, applied_at, disbursed_at, due_date, fully_repaid_at',
    )
    .order('applied_at', { ascending: false });
  if (error) return err('internal_error');
  const rows = (data ?? []) as unknown as LoanRow[];
  return ok(rows.map(mapLoanRow));
}
